import { API_BASE } from './api-base';

export interface Flow {
  name: string;
  display_name: string;
  description: string;
  flow_type?: string;
  script?: string;
}

export async function fetchFlows(
  aurigaUid: string,
  sessionToken: string,
): Promise<Flow[]> {
  const resp = await fetch(`${API_BASE}/api/v1/users/${aurigaUid}/flows`, {
    headers: { Authorization: `Bearer ${sessionToken}` },
  });
  if (!resp.ok) return [];
  const data = await resp.json();
  return data.flows ?? [];
}

export async function createConversation(
  aurigaUid: string,
  sessionToken: string,
  flow: string,
  params?: Record<string, string>,
  channelId?: string | null,
  depends?: string[],
  appKey?: string,
): Promise<string | null> {
  const body: Record<string, unknown> = { flow };
  if (channelId !== null) body.channel_id = channelId ?? `web_${aurigaUid}`;
  if (params) body.params = params;
  if (depends && depends.length > 0) body.depends = depends;
  if (appKey) body.app_key = appKey;
  const resp = await fetch(`${API_BASE}/api/v1/users/${aurigaUid}/conversations`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${sessionToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!resp.ok) return null;
  const data = await resp.json();
  return data.conversation_id ?? null;
}

export async function sendInitMessage(
  aurigaUid: string,
  conversationId: string,
  sessionToken: string,
): Promise<string | null> {
  const resp = await fetch(
    `${API_BASE}/api/v1/users/${aurigaUid}/conversations/${conversationId}/messages`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${sessionToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ is_init: true }),
    },
  );
  if (!resp.ok) return null;
  const data = await resp.json();
  return (data.request_id as string) ?? null;
}

/**
 * Subscribe to the SSE event stream for a given request and resolve
 * when a `done` or `error` event arrives. Returns the done text or null.
 */
export async function waitForInitDone(
  aurigaUid: string,
  conversationId: string,
  sessionToken: string,
  requestId: string,
  timeoutMs: number = 15000,
): Promise<string | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const { parseSSE } = await import('@readyloop/sdk');
    const resp = await fetch(
      `${API_BASE}/api/v1/users/${aurigaUid}/conversations/${conversationId}/events?request_id=${requestId}`,
      {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
          Accept: 'text/event-stream',
        },
        signal: controller.signal,
      },
    );
    if (!resp.ok || !resp.body) return null;

    const reader = resp.body.getReader();
    try {
      for await (const sseEvent of parseSSE(reader, controller.signal)) {
        if (sseEvent.event === 'done') {
          return (sseEvent.data.text as string) ?? null;
        }
        if (sseEvent.event === 'error') {
          return null;
        }
      }
    } finally {
      reader.cancel();
    }
    return null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export async function sendMessage(
  aurigaUid: string,
  conversationId: string,
  sessionToken: string,
  text: string,
): Promise<void> {
  await fetch(
    `${API_BASE}/api/v1/users/${aurigaUid}/conversations/${conversationId}/messages`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${sessionToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ text }),
    },
  );
}

export async function pushChannel(
  aurigaUid: string,
  sessionToken: string,
  channelId: string,
  flow: string,
): Promise<string | null> {
  const resp = await fetch(
    `${API_BASE}/api/v1/users/${aurigaUid}/channels/${channelId}/stack`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${sessionToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ flow }),
    },
  );
  if (!resp.ok) return null;
  const data = await resp.json();
  return data.conversation_id ?? null;
}

export async function popChannel(
  aurigaUid: string,
  sessionToken: string,
  channelId: string,
): Promise<string | null> {
  const resp = await fetch(
    `${API_BASE}/api/v1/users/${aurigaUid}/channels/${channelId}/stack/top`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${sessionToken}`,
        'Content-Type': 'application/json',
      },
    },
  );
  if (!resp.ok) return null;
  const data = await resp.json();
  return data.conversation_id ?? null;
}

export async function newChannelConversation(
  aurigaUid: string,
  sessionToken: string,
  channelId: string,
  flow: string = 'assistant',
): Promise<string | null> {
  const resp = await fetch(
    `${API_BASE}/api/v1/users/${aurigaUid}/channels/${channelId}/stack`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${sessionToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ flow }),
    },
  );
  if (!resp.ok) return null;
  const data = await resp.json();
  return data.conversation_id ?? null;
}

export async function describeFlow(
  aurigaUid: string,
  sessionToken: string,
  name: string,
): Promise<Flow | null> {
  const resp = await fetch(
    `${API_BASE}/api/v1/users/${aurigaUid}/flows/${name}`,
    { headers: { Authorization: `Bearer ${sessionToken}` } },
  );
  if (!resp.ok) return null;
  return await resp.json();
}

export async function deleteFlowApi(
  aurigaUid: string,
  sessionToken: string,
  name: string,
): Promise<{ deleted: boolean; name: string } | null> {
  const resp = await fetch(
    `${API_BASE}/api/v1/users/${aurigaUid}/flows/${name}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${sessionToken}` },
    },
  );
  if (!resp.ok) return null;
  return await resp.json();
}

export interface SkillInfo {
  name: string;
  slug: string;
  marketplace: string;
  description: string;
  command: string;
  latest_version: string | null;
  has_draft: boolean;
  owner_email: string | null;
}

export interface SkillWelcomeCard {
  icon: string;
  label: string;
  prompt: string;
}

export interface SkillMeta {
  marketplace: string;
  slug: string;
  display_name: string;
  description: string;
  welcome_cards: SkillWelcomeCard[];
}

export async function fetchSkillMeta(
  aurigaUid: string,
  sessionToken: string,
  marketplace: string,
  slug: string,
): Promise<SkillMeta | null> {
  const resp = await fetch(
    `${API_BASE}/api/v1/users/${aurigaUid}/skills/${marketplace}/${slug}/meta`,
    { headers: { Authorization: `Bearer ${sessionToken}` } },
  );
  if (!resp.ok) return null;
  return await resp.json();
}

export async function fetchSkills(
  aurigaUid: string,
  sessionToken: string,
): Promise<SkillInfo[]> {
  const resp = await fetch(`${API_BASE}/api/v1/users/${aurigaUid}/skills`, {
    headers: { Authorization: `Bearer ${sessionToken}` },
  });
  if (!resp.ok) return [];
  const data = await resp.json();
  return data.skills ?? [];
}

export async function deleteSkillApi(
  aurigaUid: string,
  sessionToken: string,
  name: string,
): Promise<{ deleted: boolean; name: string } | null> {
  const resp = await fetch(
    `${API_BASE}/api/v1/users/${aurigaUid}/skills/${name}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${sessionToken}` },
    },
  );
  if (!resp.ok) return null;
  return await resp.json();
}

// ---------------------------------------------------------------------------
// Skill sharing
// ---------------------------------------------------------------------------

export interface ShareInfo {
  id: string;
  marketplace: string;
  slug: string;
  recipient_email: string | null;
  recipient_display_name: string | null;
  recipient_is_registered: boolean;
  state: string;
  created_at: string;
}

export interface PendingShareInfo {
  id: string;
  slug: string;
  marketplace: string;
  skill_name: string;
  skill_description: string;
  owner_email: string | null;
  created_at: string;
}

export interface SharedSkillInfo {
  share_id: string;
  name: string;
  slug: string;
  marketplace: string;
  description: string;
  latest_version: string | null;
  owner_email: string | null;
  shared_at: string;
}

export async function createShare(
  aurigaUid: string,
  sessionToken: string,
  slug: string,
  recipientEmail: string,
): Promise<{ data?: ShareInfo; error?: string }> {
  const resp = await fetch(
    `${API_BASE}/api/v1/users/${aurigaUid}/skills/${slug}/shares`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${sessionToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ recipient_email: recipientEmail }),
    },
  );
  if (!resp.ok) {
    const body = await resp.json().catch(() => null);
    return { error: body?.detail ?? 'Failed to share skill.' };
  }
  return { data: await resp.json() };
}

export async function listSharesForSkill(
  aurigaUid: string,
  sessionToken: string,
  slug: string,
): Promise<ShareInfo[]> {
  const resp = await fetch(
    `${API_BASE}/api/v1/users/${aurigaUid}/skills/${slug}/shares`,
    { headers: { Authorization: `Bearer ${sessionToken}` } },
  );
  if (!resp.ok) return [];
  const data = await resp.json();
  return data.shares ?? [];
}

export async function revokeShare(
  aurigaUid: string,
  sessionToken: string,
  slug: string,
  shareId: string,
): Promise<boolean> {
  const resp = await fetch(
    `${API_BASE}/api/v1/users/${aurigaUid}/skills/${slug}/shares/${shareId}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${sessionToken}` },
    },
  );
  return resp.ok;
}

export async function listPendingShares(
  aurigaUid: string,
  sessionToken: string,
): Promise<PendingShareInfo[]> {
  const resp = await fetch(
    `${API_BASE}/api/v1/users/${aurigaUid}/shares/pending`,
    { headers: { Authorization: `Bearer ${sessionToken}` } },
  );
  if (!resp.ok) return [];
  const data = await resp.json();
  return data.shares ?? [];
}

export async function listAcceptedShares(
  aurigaUid: string,
  sessionToken: string,
): Promise<SharedSkillInfo[]> {
  const resp = await fetch(
    `${API_BASE}/api/v1/users/${aurigaUid}/shares/accepted`,
    { headers: { Authorization: `Bearer ${sessionToken}` } },
  );
  if (!resp.ok) return [];
  const data = await resp.json();
  return data.skills ?? [];
}

export async function respondToShare(
  aurigaUid: string,
  sessionToken: string,
  shareId: string,
  action: 'accept' | 'reject',
): Promise<boolean> {
  const resp = await fetch(
    `${API_BASE}/api/v1/users/${aurigaUid}/shares/${shareId}/respond`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${sessionToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ action }),
    },
  );
  return resp.ok;
}

// ---------------------------------------------------------------------------
// Agents
// ---------------------------------------------------------------------------

export interface AgentInfo {
  slug: string;
  name: string;
  description: string;
  agent_type: 'skill_agent' | 'general';
  source_skill_slug: string | null;
  source_skill_marketplace: string | null;
  icon: string;
  latest_version: string | null;
  has_draft: boolean;
  owner_email: string | null;
}

export interface AgentVersionInfo {
  version: string;
  skills: { marketplace: string; slug: string; version: string }[];
  requires: Record<string, unknown> | null;
  published_at: string;
}

export interface AgentDetail extends AgentInfo {
  versions: AgentVersionInfo[];
  soul: string | null;
}

export async function fetchAgents(
  aurigaUid: string,
  sessionToken: string,
): Promise<AgentInfo[]> {
  const resp = await fetch(`${API_BASE}/api/v1/users/${aurigaUid}/agents`, {
    headers: { Authorization: `Bearer ${sessionToken}` },
  });
  if (!resp.ok) return [];
  const data = await resp.json();
  return data.agents ?? [];
}

export async function fetchAgentDetail(
  aurigaUid: string,
  sessionToken: string,
  slug: string,
): Promise<AgentDetail | null> {
  const resp = await fetch(`${API_BASE}/api/v1/users/${aurigaUid}/agents/${slug}`, {
    headers: { Authorization: `Bearer ${sessionToken}` },
  });
  if (!resp.ok) return null;
  return await resp.json();
}

export async function fetchSharedAgentDetail(
  aurigaUid: string,
  sessionToken: string,
  marketplace: string,
  slug: string,
): Promise<AgentDetail | null> {
  const resp = await fetch(
    `${API_BASE}/api/v1/users/${aurigaUid}/agents/shared/${marketplace}/${slug}`,
    { headers: { Authorization: `Bearer ${sessionToken}` } },
  );
  if (!resp.ok) return null;
  return await resp.json();
}

export interface FileShare {
  id: string;
  vfs_path: string;
  token_prefix: string;
  url: string;
  created_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  max_downloads: number | null;
  download_count: number;
}

export interface CreateFileShareResponse extends FileShare {
  token: string;
}

export async function createFileShare(
  aurigaUid: string,
  sessionToken: string,
  vfsPath: string,
  options?: { expiresAt?: string | null; maxDownloads?: number | null },
): Promise<CreateFileShareResponse> {
  const body: Record<string, unknown> = { vfs_path: vfsPath };
  if (options?.expiresAt) body.expires_at = options.expiresAt;
  if (options?.maxDownloads != null) body.max_downloads = options.maxDownloads;
  const resp = await fetch(
    `${API_BASE}/api/v1/users/${aurigaUid}/ion/shares`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${sessionToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
  );
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || `Failed to create share (${resp.status})`);
  }
  return await resp.json();
}

export async function listFileShares(
  aurigaUid: string,
  sessionToken: string,
): Promise<FileShare[]> {
  const resp = await fetch(
    `${API_BASE}/api/v1/users/${aurigaUid}/ion/shares`,
    { headers: { Authorization: `Bearer ${sessionToken}` } },
  );
  if (!resp.ok) return [];
  const data = await resp.json();
  return data.shares ?? [];
}

export async function getFileShareByVfsPath(
  aurigaUid: string,
  sessionToken: string,
  vfsPath: string,
): Promise<FileShare | null> {
  const qs = new URLSearchParams({ vfs_path: vfsPath });
  const resp = await fetch(
    `${API_BASE}/api/v1/users/${aurigaUid}/ion/shares/by-path?${qs}`,
    { headers: { Authorization: `Bearer ${sessionToken}` } },
  );
  if (resp.status === 404) return null;
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || `Failed to look up share (${resp.status})`);
  }
  return await resp.json();
}

export async function revokeFileShare(
  aurigaUid: string,
  sessionToken: string,
  shareId: string,
): Promise<boolean> {
  const resp = await fetch(
    `${API_BASE}/api/v1/users/${aurigaUid}/ion/shares/${shareId}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${sessionToken}` },
    },
  );
  return resp.ok;
}

export async function pushChannelWithSkills(
  aurigaUid: string,
  sessionToken: string,
  channelId: string,
  skills: string[],
): Promise<string | null> {
  const resp = await fetch(
    `${API_BASE}/api/v1/users/${aurigaUid}/channels/${channelId}/stack`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${sessionToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ flow: 'assistant', skills }),
    },
  );
  if (!resp.ok) return null;
  const data = await resp.json();
  return data.conversation_id ?? null;
}
